import * as styles from "./ContactDetail.css";

import { useAtomValue, useSetAtom } from "jotai";
import { useRef, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { Spinner } from "@frontend/components/atoms/Spinner";
import {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@frontend/components/atoms/Tabs";
import { Trans } from "@lingui/react/macro";

import { useAddressBookActions } from "../../hooks/useAddressBookActions";
import { useContactForm } from "../../hooks/useContactForm";
import {
  addressBookAtom,
  isSavingContactAtom,
  modeAtom,
  paneAtom,
  selectedContactAtom,
  showListPaneAtom,
} from "../../stores";
import { ContactRelations } from "../ContactRelations";
import { Panel, PanelFooter } from "../Panel";
import { EmailList } from "./EmailList";
import { Header } from "./Header";
import { PhoneList } from "./PhoneList";
import { TypedAddressList } from "./TypedAddressList";
// import { TestDesign } from "./TestDesign";
import { UrlList } from "./UrlList";

type ContactDetailTab = "contact" | "relations";

export function ContactDetail() {
  const { setMode, createContact, updateContact } = useAddressBookActions();
  const { contactForm, submit } = useContactForm();

  const mode = useAtomValue(modeAtom);
  const selectedContact = useAtomValue(selectedContactAtom);
  const onChooserCallback = useAtomValue(addressBookAtom).onChooserCallback;
  const isSaving = useAtomValue(isSavingContactAtom);
  const pane = useAtomValue(paneAtom);

  const showListPane = useSetAtom(showListPaneAtom);

  const hasContact = selectedContact !== null;

  const [tab, setTab] = useState<ContactDetailTab>("contact");
  const [openedContactId, setOpenedContactId] = useState(selectedContact?.id);

  // Opening another contact lands on its card, not on whichever tab was left
  // selected. Saving the current one keeps the same id, so it stays put.
  if (selectedContact?.id !== openedContactId) {
    setOpenedContactId(selectedContact?.id);
    setTab("contact");
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();

    if (!selectedContact) {
      return;
    }

    const submitResult = await submit();

    // console.log("Form submit result:", submitResult);

    if (submitResult.success) {
      if (selectedContact.id === 0) {
        await createContact(submitResult.data);
      } else {
        await updateContact(submitResult.data);

        setMode("view");
      }
    }
  };

  const handleSelect = () => {
    if (selectedContact && onChooserCallback) {
      onChooserCallback(selectedContact);
    }
  };

  return (
    <Panel
      className={styles.panel}
      hiddenBelow={pane === "detail" ? "none" : "mobile"}
    >
      <Tabs
        className={styles.tabs}
        variant="underline"
        size="sm"
        value={tab}
        onValueChange={(value) => setTab(value as ContactDetailTab)}
      >
        <div className={styles.title}>
          <Button
            variant="ghost"
            size="sm"
            className={styles.backButton}
            onClick={() => showListPane()}
          >
            <Icon name="ChevronLeft" size="md" />
            <Trans>Contacts</Trans>
          </Button>

          {hasContact && (
            <TabsList>
              <TabsTab value="contact">
                <Trans>Contact</Trans>
              </TabsTab>

              <TabsTab value="relations">
                <Trans>Relations</Trans>
              </TabsTab>

              <TabsIndicator />
            </TabsList>
          )}
        </div>

        {hasContact ? (
          <TabsContent className={styles.tabsContent}>
            <TabsPanel value="contact">
              <ScrollArea className={styles.scrollArea}>
                <ScrollAreaViewport fade>
                  <ScrollAreaContent
                    className={styles.content}
                    ref={containerRef}
                  >
                    <form
                      ref={formRef}
                      className={styles.form}
                      onSubmit={handleSubmit}
                    >
                      {/* <TestDesign /> */}

                      <Header />

                      {/* Date de naissance */}

                      <PhoneList />
                      <EmailList />
                      <UrlList />
                      <TypedAddressList />
                    </form>
                  </ScrollAreaContent>
                </ScrollAreaViewport>

                <ScrollAreaScrollbar alwaysVisible expandOnHover>
                  <ScrollAreaThumb />
                </ScrollAreaScrollbar>
              </ScrollArea>
            </TabsPanel>

            <TabsPanel value="relations">
              <ContactRelations />
            </TabsPanel>
          </TabsContent>
        ) : (
          <div className={styles.empty}>
            <Icon name="ContactRound" size="lg" />
            <Trans>No contact selected</Trans>
          </div>
        )}
      </Tabs>

      <PanelFooter className={styles.footer}>
        {hasContact && tab === "contact" && (
          <Button
            onClick={() => {
              if (mode === "edit") {
                formRef.current?.requestSubmit();
              } else {
                setMode("edit");
              }
            }}
            disabled={isSaving}
          >
            {mode === "edit" ? (
              isSaving ? (
                <Spinner />
              ) : (
                <Icon name="Save" />
              )
            ) : (
              <Icon name="UserPen" />
            )}
          </Button>
        )}

        {mode === "select" && (
          <Button onClick={handleSelect} disabled={!selectedContact}>
            Select
            <Icon name="ContactRound" />
          </Button>
        )}
      </PanelFooter>
    </Panel>
  );
}
